<template>
  <section
    v-if="showBlock"
    class="pp-dv"
  >
    <div class="head">
      <div>
        <div class="eyebrow">◆ {{ t('property.developerVaultSection.eyebrow') }}</div>
        <h2>{{ t('property.developerVaultSection.moreOn', { developerName }) }}</h2>
        <p class="sub">{{ t('property.developerVaultSection.subtitle') }}</p>
      </div>
      <NuxtLink
        :to="'/developer-vault'"
        class="see"
      >
        {{ t('property.developerVaultSection.openVault') }}
        <span
          class="inline-block rtl:-scale-x-100"
          aria-hidden="true"
          >→</span
        >
      </NuxtLink>
    </div>
    <div
      v-if="isLoading"
      class="loading"
    >
      <UIProgressSpinner
        width="40"
        height="40"
      />
    </div>
    <div
      v-else
      class="grid"
    >
      <TrainingVideoCard
        v-for="v in visibleItems"
        :key="v.id"
        :video="v"
        :unlocked="isUnlocked(v)"
        :watched="isWatched(v.id)"
        @watch="onWatch"
      />
    </div>
    <TrainingUpgradeModal v-model="showUpgrade" />
    <TrainingExclusiveModal
      v-model="showExclusiveModal"
      :video="selectedVideo"
      @upgrade="showUpgrade = true"
    />
    <TrainingVideoModal
      v-model="showVideoModal"
      :video="selectedVideo"
      :unlocked="selectedVideo ? isUnlocked(selectedVideo) : false"
      @mark-watched="markWatched"
      @upgrade="showUpgrade = true"
    />
  </section>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import TrainingVideoCard from '@/components/training/VideoCard.vue';
import TrainingUpgradeModal from '@/components/training/UpgradeModal.vue';
import TrainingExclusiveModal from '@/components/training/ExclusiveModal.vue';
import TrainingVideoModal from '@/components/training/VideoModal.vue';
import type { TrainingVideo } from '@/composables/useTraining';
import { briefingLessonPath, showProjectBriefings } from '~/utils/training/projectBriefings';

const props = defineProps<{
  // Postgres projects.id (raw, no geniemap COALESCE, no p- prefix). The
  // training endpoint matches projectIds[] against this canonical key.
  projectId: string;
  developerName: string;
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const { width } = useWindowSize();
const isLargeScreen = computed(() => width.value >= 1024);

const showUpgrade = ref(false);
const showExclusiveModal = ref(false);
const showVideoModal = ref(false);
const selectedVideo = ref<TrainingVideo | null>(null);

const watchedIds = ref<string[]>([]);
const WATCHED_KEY = 'training_watched_v1';

const loadWatched = () => {
  try {
    const raw = process.client ? localStorage.getItem(WATCHED_KEY) : null;
    watchedIds.value = raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    watchedIds.value = [];
  }
};

const persistWatched = () => {
  if (process.client) {
    localStorage.setItem(WATCHED_KEY, JSON.stringify(watchedIds.value));
  }
};

onMounted(() => loadWatched());

const {
  data: payload,
  isLoading,
  isFetched,
} = useQuery({
  queryKey: computed(() => ['training', 'by-project', props.projectId]),
  queryFn: () =>
    $fetch<{ items: TrainingVideo[] }>(`/api/training/by-project/${props.projectId}`, {
      headers: { Authorization: `Bearer ${authStore.token}` },
    }),
  enabled: computed(() => !!props.projectId && !!authStore.token),
});

const items = computed<TrainingVideo[]>(() => payload.value?.items || []);
const visibleItems = computed(() => items.value.slice(0, 3));

const showBlock = computed(() =>
  showProjectBriefings({
    projectId: props.projectId,
    signedIn: !!authStore.token,
    isLoading: isLoading.value,
    isFetched: isFetched.value,
    count: items.value.length,
  }),
);

const isUnlocked = (v: TrainingVideo) => {
  if (v.hasAccess !== undefined && v.hasAccess !== null) return Boolean(v.hasAccess);
  return true;
};

const isWatched = (id: string) => watchedIds.value.includes(id);

const markWatched = (id: string) => {
  if (!watchedIds.value.includes(id)) {
    watchedIds.value.push(id);
    persistWatched();
  }
};

const gotoVideo = (v: TrainingVideo) => {
  navigateTo(briefingLessonPath(v));
};

function onWatch(v: TrainingVideo) {
  if (isUnlocked(v)) {
    if (isLargeScreen.value) {
      selectedVideo.value = v;
      showVideoModal.value = true;
    } else {
      gotoVideo(v);
    }
  } else if (isLargeScreen.value) {
    selectedVideo.value = v;
    showExclusiveModal.value = true;
  } else {
    showUpgrade.value = true;
  }
}

watch(showVideoModal, (open) => {
  if (!open) loadWatched();
});
</script>

<style scoped lang="scss">
.pp-dv {
  margin-top: 40px;
  padding-top: 36px;
  border-top: 1px solid rgba(13, 15, 21, 0.08);
}

.head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #8b6000;
    font-weight: 700;
    margin-bottom: 8px;
  }

  h2 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 32px;
    letter-spacing: -0.02em;
    line-height: 1;
    margin: 0;
  }

  .sub {
    font-size: 14px;
    color: rgba(13, 15, 21, 0.62);
    margin-top: 8px;
    max-width: 560px;
  }

  .see {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #0d0f15;
    text-transform: uppercase;
    padding: 10px 14px;
    border: 1px solid #0d0f15;
    border-radius: 10px;
    transition: all 0.15s ease;

    &:hover {
      background: #0d0f15;
      color: white;
    }
  }
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 744px) {
    grid-template-columns: 1fr;
  }
}

.loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
</style>
